import { useState } from 'react';

export const T12FinalCTA = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    units: '',
    currentNOI: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        propertyAddress: formData.propertyAddress,
        units: formData.units,
        currentNOI: formData.currentNOI,
        timeline: formData.timeline,
        message: formData.message
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/t12-consultation-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', propertyAddress: '', units: '', currentNOI: '', timeline: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Ready to Maximize Your Property's Value?
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Get expert T12 analysis and a cash offer for your apartment building. We understand the numbers and can close quickly, regardless of your T12 situation.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold mb-6">Why Work With Us?</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-chart-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2">We Understand T12</h4>
                    <p className="text-gray-300 text-sm">
                      We're experienced investors who analyze T12 statements daily. We understand the numbers, see the potential, and make fair offers based on actual performance.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2">Cash Offers</h4>
                    <p className="text-gray-300 text-sm">
                      No financing contingencies, no appraisal issues, no lender delays. We buy with cash and can close in as little as 7-14 days.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2">No T12 Problems</h4>
                    <p className="text-gray-300 text-sm">
                      Weak T12? High vacancy? Deferred maintenance? We specialize in value-add properties and can work with challenging situations.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2">Fast Process</h4>
                    <p className="text-gray-300 text-sm">
                      We review your T12 within 24 hours and provide a preliminary offer within 48 hours. No lengthy due diligence or endless negotiations.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-hand-heart-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2">Flexible Terms</h4>
                    <p className="text-gray-300 text-sm">
                      Need to close quickly? Want a longer timeline? Prefer seller financing? We can structure the deal to meet your specific needs.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <h3 className="text-xl font-bold mb-4">What Happens Next?</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Submit Your Information</div>
                    <p className="text-sm text-gray-300">Fill out the form with basic property details</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">T12 Review</div>
                    <p className="text-sm text-gray-300">We analyze your T12 and property details within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Receive Cash Offer</div>
                    <p className="text-sm text-gray-300">Get a fair, no-obligation cash offer within 48 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Close on Your Timeline</div>
                    <p className="text-sm text-gray-300">Choose your closing date - as fast as 7 days or longer if needed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-2">Get Your Free T12 Analysis & Cash Offer</h3>
            <p className="text-gray-300 mb-6">No obligation. Confidential. Fast response.</p>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="t12-final-cta-form">
              <div>
                <label className="block text-sm font-semibold mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="John Smith"
                />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Property Address *</label>
                <input
                  type="text"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="123 Main St, City, State"
                />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Number of Units *</label>
                  <input
                    type="number"
                    name="units"
                    value={formData.units}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="24"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Current T12 NOI (if known)</label>
                  <input
                    type="text"
                    name="currentNOI"
                    value={formData.currentNOI}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="$180,000"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">When do you want to sell? *</label>
                <select
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                >
                  <option value="" className="bg-slate-800">Select timeline...</option>
                  <option value="asap" className="bg-slate-800">As soon as possible</option>
                  <option value="1-3months" className="bg-slate-800">1-3 months</option>
                  <option value="3-6months" className="bg-slate-800">3-6 months</option>
                  <option value="6-12months" className="bg-slate-800">6-12 months</option>
                  <option value="exploring" className="bg-slate-800">Just exploring options</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Additional Information</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none text-sm"
                  placeholder="Tell us about your property's T12 situation, any challenges, or specific questions..."
                ></textarea>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Get Free T12 Analysis & Cash Offer'}
              </button>
              
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 text-sm">
                  <strong>Thank you!</strong> We've received your information and will review your T12 within 24 hours. Expect a call or email with your preliminary cash offer within 48 hours.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  Something went wrong. Please call us directly at <strong>(123) 456-7890</strong> or email info@sellapartmentscash.com
                </div>
              )}
              
              <p className="text-xs text-gray-400 text-center">
                By submitting this form, you agree to be contacted about your property. We respect your privacy and will never share your information.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};